import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import bindAll from 'lodash.bindall';

import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';
import Dots from '../connection-modal/dots.jsx';
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';

import styles from '../connection-modal/connection-modal.css';
import hwStyles from './hardware-connection-modal.css';

import hardwareUploader, { BOARD_CONFIGS } from '../../lib/hardware-uploader';
import {generateCode} from '../../lib/code-generator';
import customExtensions from '../../lib/custom-extensions';

import radarIcon from '../connection-modal/icons/searching.png';
import refreshIcon from '../connection-modal/icons/refresh.svg';

import unoIcon from './icons/uno.png';
import nanoIcon from './icons/nano.png';
import megaIcon from './icons/mega.png';
import picoIcon from './icons/pico.png';
import esp32Icon from './icons/esp32.png';

const messages = defineMessages({
    title: {
        id: 'gui.hardwareModal.title',
        defaultMessage: 'Upload to Hardware',
        description: 'Title for the hardware upload modal'
    },
    trainerTitle: {
        id: 'gui.hardwareModal.trainerTitle',
        defaultMessage: 'Connect Trainer Device',
        description: 'Title for the trainer device connection modal'
    }
});

const TRAINER_DEVICES = [
    {id: 'trainer_kit_1_1', label: 'Trainer Kit 1.1 (Level 1)'},
    {id: 'trainer_kit_1_2', label: 'Trainer Kit 1.2 (Level 2)'},
    {id: 'trainer_kit_1_3', label: 'Trainer Kit 1.3 (Level 3)'},
    {id: 'trainer_kit_1_4', label: 'Trainer Kit 1.4 (Level 4)'},
    {id: 'trainer_kit_1_5', label: 'Trainer Kit 1.5 (Level 5)'},
    {id: 'trainer_kit_1_6', label: 'Trainer Kit 1.6 (Level 6)'},
    {id: 'trainer_kit_1_7', label: 'Trainer Kit 1.7 (Level 7)'}
].map(device => Object.assign({}, device, {
    icon: unoIcon,
    type: 'arduino',
    fqbn: 'arduino:avr:uno'
}));

const iconMap = {
    arduino_uno: unoIcon,
    arduino_nano: nanoIcon,
    arduino_mega: megaIcon,
    rpi_pico: picoIcon,
    esp32: esp32Icon
};

const DEVICES = BOARD_CONFIGS.map(config => Object.assign({}, config, {
    icon: iconMap[config.id] || unoIcon
}));

// Which dot step index maps to each phase
const PHASE_SELECT = 'select'; // dot 0
const PHASE_SCANNING = 'scanning'; // dot 1
const PHASE_CONNECTED = 'connected'; // dot 2

const PHASE_DOT = {
    [PHASE_SELECT]: 0,
    [PHASE_SCANNING]: 1,
    [PHASE_CONNECTED]: 2
};

class HardwareConnectionModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSelectDevice',
            'handleConnect',
            'handleDisconnect',
            'handleUpload',
            'handleViewCode',
            'handleCopyCode',
            'handleBack',
            'handleDiagnostic',
            'toggleDiagnostics'
        ]);

        const connected = hardwareUploader.isConnected;
        const currentType = hardwareUploader.deviceType;
        const currentFqbn = hardwareUploader.boardFqbn;
        const devices = props.trainerMode ? TRAINER_DEVICES : DEVICES;

        let selectedDevice = null;
        if (currentType) {
            selectedDevice = devices.find(d =>
                d.type === currentType && (d.type === 'micropython' ? true : d.fqbn === currentFqbn)
            ) || null;
        }

        this.state = {
            phase: connected ? PHASE_CONNECTED : PHASE_SELECT,
            selectedDevice: selectedDevice,
            uploading: false,
            progress: 0,
            statusMessage: connected ? 'Connected! Ready to upload.' : '',
            generatedCode: '',
            showCode: false,
            copySuccess: false,
            error: null,
            isShaking: false,
            diagnostics: [],
            showDiagnostics: false
        };
    }

    componentDidMount () {
        hardwareUploader.addEventListener('diagnostic', this.handleDiagnostic);
    }

    componentWillUnmount () {
        hardwareUploader.removeEventListener('diagnostic', this.handleDiagnostic);
    }

    handleDiagnostic (event) {
        this.setState(prevState => ({
            diagnostics: [...prevState.diagnostics, event.detail]
        }));
    }

    toggleDiagnostics () {
        this.setState(prevState => ({ showDiagnostics: !prevState.showDiagnostics }));
    }

    handleSelectDevice (device) {
        hardwareUploader.setDeviceType(device.type, device.fqbn);
        this.setState({selectedDevice: device, error: null});
    }

    async handleConnect () {
        this.setState({phase: PHASE_SCANNING, error: null, statusMessage: ''});
        try {
            await hardwareUploader.connect();
            this.setState({phase: PHASE_CONNECTED, statusMessage: 'Connected! Ready to upload.'});
        } catch (err) {
            this.setState({error: err.message, statusMessage: 'Connection failed.'});
        }
    }

    async handleDisconnect () {
        await hardwareUploader.disconnect();
        this.setState({
            phase: PHASE_SELECT,
            statusMessage: '',
            generatedCode: '',
            showCode: false,
            error: null
        });
    }

    handleBack () {
        this.setState({phase: PHASE_SELECT, error: null, statusMessage: ''});
    }

    handleViewCode () {
        const {vm} = this.props;
        const {selectedDevice} = this.state;
        if (!selectedDevice) return;
        const code = generateCode(vm, selectedDevice.type);
        this.setState({generatedCode: code, showCode: true});
    }

    handleCopyCode () {
        if (this.state.generatedCode) {
            navigator.clipboard.writeText(this.state.generatedCode).then(() => {
                this.setState({copySuccess: true});
                setTimeout(() => this.setState({copySuccess: false}), 2000);
            });
        }
    }

    async handleUpload () {
        const {vm} = this.props;
        const {selectedDevice} = this.state;
        if (!selectedDevice) return;

        this.setState({
            uploading: true, 
            progress: 0, 
            error: null, 
            statusMessage: 'Generating code…',
            diagnostics: [] // Clear diagnostics on new upload
        });

        const code = generateCode(vm, selectedDevice.type);
        this.setState({generatedCode: code});

        let libraries = [];
        if (vm && vm.extensionManager) {
            const loadedExtIds = Array.from(vm.extensionManager._loadedExtensions.keys());
            loadedExtIds.forEach(extId => {
                const ext = customExtensions[extId];
                if (ext) {
                    const info = typeof ext.prototype.getSensorInfo === 'function' 
                        ? new ext(vm.runtime).getSensorInfo() 
                        : null;
                    if (info && info.arduinoDependencies) {
                        libraries = libraries.concat(info.arduinoDependencies);
                    }
                }
            });
        }
        libraries = [...new Set(libraries)]; // Unique

        try {
            const result = await hardwareUploader.upload(code, libraries, (pct, msg) => {
                this.setState({progress: pct, statusMessage: msg});
            });

            this.setState({
                uploading: false,
                progress: result.success ? 100 : 0,
                statusMessage: result.message,
                error: result.success ? null : result.message
            });
        } catch (err) {
            this.setState({
                uploading: false,
                progress: 0,
                statusMessage: 'Upload failed',
                error: err.message
            });
        }
    }

    renderSelectPhase () {
        const {selectedDevice} = this.state;
        const devices = this.props.trainerMode ? TRAINER_DEVICES : DEVICES;
        return (
            <Box className={styles.body}>
                {/* Activity area: board grid */}
                <Box className={classNames(styles.activityArea, hwStyles.activityAreaOverride)}>
                    <div className={hwStyles.deviceGrid}>
                        {devices.map(device => (
                            <div
                                key={device.id}
                                className={hwStyles.deviceItem}
                            >
                                <button
                                    className={classNames(hwStyles.deviceTile, {
                                        [hwStyles.deviceTileSelected]:
                                            selectedDevice && selectedDevice.id === device.id
                                    })}
                                    onClick={() => this.handleSelectDevice(device)}
                                >
                                    <img
                                        src={device.icon}
                                        className={hwStyles.deviceImage}
                                    />
                                </button>
                                <span className={hwStyles.deviceLabel}>{device.label}</span>
                            </div>
                        ))}
                    </div>
                </Box>

                {/* Bottom area */}
                <Box className={styles.bottomArea}>
                    <Box className={classNames(styles.bottomAreaItem, styles.instructions)}>
                        {this.props.trainerMode ? (
                            <FormattedMessage
                                defaultMessage="Select your trainer kit above, then click Start Scanning."
                                description="Instructions for selecting a trainer hardware kit"
                                id="gui.hardwareModal.trainerSelectInstructions"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Select your board above, then click Start Scanning."
                                description="Instructions for selecting a hardware board"
                                id="gui.hardwareModal.selectInstructions"
                            />
                        )}
                    </Box>
                    <Dots
                        className={styles.bottomAreaItem}
                        counter={PHASE_DOT[PHASE_SELECT]}
                        total={3}
                    />
                    <Box className={classNames(styles.bottomAreaItem, styles.buttonRow)}>
                        {(!this.props.trainerMode && process.env.UNLOCK_HARDWARE !== 'true') ? (
                            <ComingSoonTooltip
                                place="top"
                                tooltipId="hw-interface-locked"
                                customMessage="HW Interface available in advanced version V3.0 (IoT.Prarambh)"
                            >
                                <button
                                    className={classNames(styles.connectionButton, {
                                        [hwStyles.shake]: this.state.isShaking
                                    })}
                                    disabled={!selectedDevice}
                                    onClick={() => {
                                        this.setState({isShaking: true});
                                        setTimeout(() => this.setState({isShaking: false}), 400);
                                    }}
                                >
                                    <span style={{marginRight: '6px', fontSize: '1.2em'}}>🔒</span>
                                    <FormattedMessage
                                        defaultMessage="Start Scanning"
                                        description="Button to start scanning for serial port"
                                        id="gui.hardwareModal.startScanning"
                                    />
                                    <img
                                        className={styles.buttonIconRight}
                                        src={refreshIcon}
                                    />
                                </button>
                            </ComingSoonTooltip>
                        ) : (
                            <button
                                className={styles.connectionButton}
                                disabled={!selectedDevice}
                                onClick={this.handleConnect}
                            >
                                <FormattedMessage
                                    defaultMessage="Start Scanning"
                                    description="Button to start scanning for serial port"
                                    id="gui.hardwareModal.startScanning"
                                />
                                <img
                                    className={styles.buttonIconRight}
                                    src={refreshIcon}
                                />
                            </button>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    renderScanningPhase () {
        const {error} = this.state;
        return (
            <Box className={styles.body}>
                {/* Activity area: radar or error */}
                <Box className={styles.activityArea}>
                    {error ? (
                        <Box className={styles.centeredRow}>
                            <div className={hwStyles.errorMessage}>{error}</div>
                        </Box>
                    ) : (
                        <div className={styles.activityAreaInfo}>
                            <div className={styles.centeredRow}>
                                <img
                                    className={classNames(styles.radarSmall, styles.radarSpin)}
                                    src={radarIcon}
                                />
                                <FormattedMessage
                                    defaultMessage="Looking for devices"
                                    description="Text shown while scanning for serial port"
                                    id="gui.hardwareModal.lookingForDevices"
                                />
                            </div>
                        </div>
                    )}
                </Box>

                {/* Bottom area */}
                <Box className={styles.bottomArea}>
                    <Box className={classNames(styles.bottomAreaItem, styles.instructions)}>
                        {error ? (
                            <FormattedMessage
                                defaultMessage="Connection failed. Check the board and try again."
                                description="Error message for failed scan"
                                id="gui.hardwareModal.connectionFailed"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Select your device in the browser's serial port list."
                                description="Prompt for choosing serial port"
                                id="gui.hardwareModal.scanInstructions"
                            />
                        )}
                    </Box>
                    <Dots
                        className={styles.bottomAreaItem}
                        counter={PHASE_DOT[PHASE_SCANNING]}
                        error={!!error}
                        total={3}
                    />
                    <Box className={classNames(styles.bottomAreaItem, styles.cornerButtons)}>
                        <button
                            className={classNames(styles.connectionButton, styles.redButton)}
                            onClick={this.handleBack}
                        >
                            <FormattedMessage
                                defaultMessage="Back"
                                description="Button to go back to board selection"
                                id="gui.hardwareModal.back"
                            />
                        </button>
                        {error && (
                            <button
                                className={styles.connectionButton}
                                onClick={this.handleConnect}
                            >
                                <FormattedMessage
                                    defaultMessage="Try Again"
                                    description="Button to retry connecting"
                                    id="gui.hardwareModal.tryAgain"
                                />
                                <img
                                    className={styles.buttonIconRight}
                                    src={refreshIcon}
                                />
                            </button>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    renderConnectedPhase () {
        const {
            selectedDevice, uploading, progress, statusMessage, error,
            generatedCode, showCode
        } = this.state;

        return (
            <Box className={styles.body}>
                {/* Activity area: success icon + board name */}
                <Box className={styles.activityArea}>
                    <Box className={styles.centeredRow}>
                        <div className={hwStyles.connectedActivity}>
                            {selectedDevice ? (
                                <img
                                    src={selectedDevice.icon}
                                    className={hwStyles.connectedImage}
                                />
                            ) : null}
                            <div className={hwStyles.connectedBadge}>✓</div>
                        </div>
                    </Box>
                </Box>

                {/* Code preview inline below activity area if shown */}
                {showCode && generatedCode && (
                    <div className={hwStyles.codePreview}>
                        <div className={hwStyles.codeHeader}>
                            <span>Generated Code</span>
                            <button
                                className={hwStyles.copyButton}
                                onClick={this.handleCopyCode}
                            >
                                {this.state.copySuccess ? 'Copied!' : 'Copy Code'}
                            </button>
                        </div>
                        <pre className={hwStyles.codeBlock}>{generatedCode}</pre>
                    </div>
                )}

                {/* Diagnostics Toggle */}
                {this.state.diagnostics.length > 0 && (
                    <div className={hwStyles.diagnosticsToggle} onClick={this.toggleDiagnostics}>
                        {this.state.showDiagnostics ? '▼ Hide Diagnostics' : '▶ Show Diagnostics'}
                    </div>
                )}
                
                {/* Diagnostics Console */}
                {this.state.showDiagnostics && (
                    <div className={hwStyles.diagnosticsConsole}>
                        <div className={hwStyles.diagnosticsLine}><strong>Selected Board:</strong> {selectedDevice ? selectedDevice.label : 'None'}</div>
                        <div className={hwStyles.diagnosticsLine}><strong>Selected FQBN:</strong> {selectedDevice ? selectedDevice.fqbn : 'None'}</div>
                        <div className={hwStyles.diagnosticsLine}><strong>COM Port:</strong> {hardwareUploader.port ? 'Connected' : 'Disconnected'}</div>
                        {this.state.diagnostics.map((diag, i) => (
                            <div key={i} className={classNames(hwStyles.diagnosticsLine, hwStyles[`diag_${diag.type}`])}>
                                {diag.message}
                            </div>
                        ))}
                    </div>
                )}

                {/* Progress bar */}
                {uploading && (
                    <div className={hwStyles.progressBarOuter}>
                        <div
                            className={hwStyles.progressBarInner}
                            style={{width: `${progress}%`}}
                        />
                    </div>
                )}

                {/* Bottom area */}
                <Box className={styles.bottomArea}>
                    <Box className={classNames(styles.bottomAreaItem, styles.instructions)}>
                        <span className={classNames({[hwStyles.errorText]: error})}>
                            {statusMessage || `Connected to ${selectedDevice ? selectedDevice.label : 'device'}`}
                        </span>
                    </Box>
                    <Dots
                        success={!error}
                        error={!!error}
                        className={styles.bottomAreaItem}
                        total={3}
                    />
                    <Box className={classNames(styles.bottomAreaItem, styles.cornerButtons)}>
                        <button
                            className={classNames(styles.connectionButton, styles.redButton)}
                            onClick={this.handleDisconnect}
                        >
                            <FormattedMessage
                                defaultMessage="Disconnect"
                                description="Button to disconnect from hardware"
                                id="gui.hardwareModal.disconnect"
                            />
                        </button>
                        <div className={styles.buttonRow}>
                            <button
                                className={styles.connectionButton}
                                onClick={this.handleViewCode}
                            >
                                <FormattedMessage
                                    defaultMessage="View Code"
                                    description="Button to preview generated code"
                                    id="gui.hardwareModal.viewCode"
                                />
                            </button>
                            <button
                                className={styles.connectionButton}
                                disabled={uploading}
                                onClick={this.handleUpload}
                            >
                                {uploading ? (
                                    <FormattedMessage
                                        defaultMessage="Uploading…"
                                        description="Upload in progress"
                                        id="gui.hardwareModal.uploading"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Upload"
                                        description="Button to upload code to hardware"
                                        id="gui.hardwareModal.upload"
                                    />
                                )}
                            </button>
                        </div>
                    </Box>
                </Box>
            </Box>
        );
    }

    render () {
        const {phase} = this.state;
        return (
            <Modal
                className={classNames(styles.modalContent, hwStyles.modalOverride)}
                contentLabel={this.props.intl.formatMessage(
                    this.props.trainerMode ? messages.trainerTitle : messages.title
                )}
                headerClassName={styles.header}
                id={this.props.trainerMode ? 'trainerDeviceModal' : 'hardwareConnectionModal'}
                onRequestClose={this.props.onClose}
            >
                {phase === PHASE_SELECT && this.renderSelectPhase()}
                {phase === PHASE_SCANNING && this.renderScanningPhase()}
                {phase === PHASE_CONNECTED && this.renderConnectedPhase()}
            </Modal>
        );
    }
}

HardwareConnectionModal.propTypes = {
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    trainerMode: PropTypes.bool,
    vm: PropTypes.object.isRequired
};

HardwareConnectionModal.defaultProps = {
    trainerMode: false
};

export default injectIntl(HardwareConnectionModal);
